import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ChangeServiceProviderSettings, MapViewQuery, ServiceBoardQuery, ServiceBoardSortParameter, ServiceBoardState } from "./models/models";
import publicServiceRequestsApi from "./api/publicServiceRequestsApi";
import serviceProviderSettingsApi from "./api/serviceProviderSettingsApi";

export const getPublicServiceRequests = createAsyncThunk(
  "customerServiceBoard/getPublicServiceRequests",
  async (query: ServiceBoardQuery) => {
    return publicServiceRequestsApi.getServiceRequestsAsync(query);
  }
);

export const getPublicServiceRequest = createAsyncThunk(
  "customerServiceBoard/getPublicServiceRequest",
  async (id: string) => {
    return publicServiceRequestsApi.getServiceRequestAsync(id);
  }
);

export const getServiceBoardFilters = createAsyncThunk(
  "customerServiceBoard/getServiceBoardFilters",
  async () => {
    return publicServiceRequestsApi.getFiltersAsync();
  }
);

export const getServiceBoardStats = createAsyncThunk(
  "customerServiceBoard/getServiceBoardStats",
  async () => {
    return publicServiceRequestsApi.getStatsAsync();
  }
);

export const getMapViewData = createAsyncThunk(
  "customerServiceBoard/getMapViewData",
  async (query: MapViewQuery) => {
    return publicServiceRequestsApi.getMapDataAsync(query);
  }
);

export const getProviderSettings = createAsyncThunk(
  "customerServiceBoard/getProviderSettings",
  async () => {
    return serviceProviderSettingsApi.getSettingsAsync();
  }
);

export const updateProviderSettings = createAsyncThunk(
  "customerServiceBoard/updateProviderSettings",
  async (settings: ChangeServiceProviderSettings) => {
    return serviceProviderSettingsApi.updateSettingsAsync(settings);
  }
);

export const unpublishServiceRequest = createAsyncThunk(
  "customerServiceBoard/unpublishServiceRequest",
  async (id: string) => {
    await publicServiceRequestsApi.unpublishServiceRequestAsync(id);
    return id;
  }
);

const initialState: ServiceBoardState = {
  query: {
    search: undefined,
    sortBy: ServiceBoardSortParameter.openDate,
    sortDesc: true,
    pageNo: 1,
    pageSize: 20,
    starredOnly: false,
    urgentOnly: false,
    dateFrom: undefined,
    dateTo: undefined,
    machineBrandIds: undefined,
    newTodayOnly: undefined,
    expiringSoonOnly: undefined,
    withMyOffersOnly: undefined,
    priorityTypeCodes: undefined,
    serviceTypeCodes: undefined,
    countryIds: undefined,
  },
  serviceRequests: undefined,
  serviceRequest: undefined,
  filters: undefined,
  stats: undefined,
  loading: false,
  success: undefined,
  error: undefined,
  // Map view
  mapData: undefined,
  mapLoading: false,
  mapError: undefined,
  selectedCity: null,
  // Service provider settings
  providerSettings: undefined,
  providerSettingsLoading: false,
};

const customerServiceBoardSlice = createSlice({
  name: "customerServiceBoard",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    clearServiceRequest: (state) => {
      state.serviceRequest = undefined;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = undefined;
    },
    setSelectedCity: (state, action) => {
      state.selectedCity = action.payload;
    },
    clearMapError: (state) => {
      state.mapError = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getPublicServiceRequests.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPublicServiceRequests.fulfilled, (state, { payload }) => {
        state.serviceRequests = payload;
        state.loading = false;
      })
      .addCase(getPublicServiceRequests.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getPublicServiceRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPublicServiceRequest.fulfilled, (state, { payload }) => {
        state.serviceRequest = payload;
        state.loading = false;
      })
      .addCase(getPublicServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getServiceBoardFilters.fulfilled, (state, { payload }) => {
        state.filters = payload;
      });

    builder
      .addCase(getServiceBoardStats.fulfilled, (state, { payload }) => {
        state.stats = payload;
      });

    builder
      .addCase(getMapViewData.pending, (state) => {
        state.mapLoading = true;
        state.mapError = undefined;
      })
      .addCase(getMapViewData.fulfilled, (state, { payload }) => {
        state.mapData = payload;
        state.mapLoading = false;
      })
      .addCase(getMapViewData.rejected, (state, { error }) => {
        state.mapError = error;
        state.mapLoading = false;
      });

    builder
      .addCase(getProviderSettings.pending, (state) => {
        state.providerSettingsLoading = true;
      })
      .addCase(getProviderSettings.fulfilled, (state, { payload }) => {
        state.providerSettings = payload;
        state.providerSettingsLoading = false;
      })
      .addCase(getProviderSettings.rejected, (state) => {
        state.providerSettingsLoading = false;
      });

    builder
      .addCase(updateProviderSettings.fulfilled, (state, { payload }) => {
        state.providerSettings = payload;
      });

    builder
      .addCase(unpublishServiceRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(unpublishServiceRequest.fulfilled, (state, { payload: removedId }) => {
        state.loading = false;
        if (state.serviceRequests) {
          state.serviceRequests.items = state.serviceRequests.items.filter(
            (item) => item.id !== removedId
          );
          state.serviceRequests.totalCount = Math.max(0, state.serviceRequests.totalCount - 1);
        }
      })
      .addCase(unpublishServiceRequest.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  }
});

export const {
  changeQuery,
  clearServiceRequest,
  setSuccess,
  setError,
  clearError,
  setSelectedCity,
  clearMapError,
} = customerServiceBoardSlice.actions;

export default customerServiceBoardSlice.reducer;
